import { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import CreatePost from '../components/CreatePost';
import Post, { PostProps } from '../components/Post';
import StatCard from '../components/StatCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, MessageCircle, Handshake, RefreshCw } from 'lucide-react';
import { 
  getPosts, 
  getAnalyticsStats, 
  getFollowingPosts, 
  getDailyPostsData, 
  getMonthlyMembersData, 
  getCollaborationData,
  subscribeToPosts
} from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast'; // Import toast for error messages
import { supabase } from '@/integrations/supabase/client'; // Import supabase client correctly
import { Skeleton } from "@/components/ui/skeleton"; // Import Skeleton
import { useConnectionContext } from '@/contexts/ConnectionContext';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext'; // Import auth context to get current user

// Define interface for the stats we need on the Home page
interface HomeStats {
  totalMembers: number;
  totalPosts: number;
  totalCollaborations: number;
}

const Home = () => {
  const [posts, setPosts] = useState<PostProps[]>([]);
  const [followingPosts, setFollowingPosts] = useState<PostProps[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [isLoadingFollowingPosts, setIsLoadingFollowingPosts] = useState(true);
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
  const [isLoadingMoreFollowingPosts, setIsLoadingMoreFollowingPosts] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [hasMoreFollowingPosts, setHasMoreFollowingPosts] = useState(true);
  const [postsOffset, setPostsOffset] = useState(0);
  const [followingPostsOffset, setFollowingPostsOffset] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [homeStats, setHomeStats] = useState<HomeStats | null>(null);
  const [activeTab, setActiveTab] = useState('forYou');
  const { isConnected } = useConnectionContext();
  const location = useLocation();
  const highlightedPostRef = useRef<HTMLDivElement>(null);
  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(null);
  const { user } = useAuth(); // Get current user
  const postsSubscriptionRef = useRef<(() => void) | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  
  // Add state for chart data
  const [postsChartData, setPostsChartData] = useState<number[]>([]);
  const [membersChartData, setMembersChartData] = useState<number[]>([]);
  const [collaborationChartData, setCollaborationChartData] = useState<number[]>([]);
  const [isLoadingChartData, setIsLoadingChartData] = useState(true);
  
  // Format database post to match the PostProps interface
  const formatDatabasePost = useCallback((post: any): PostProps => {
    // Extract image URLs from post_images
    const imageUrls = post.post_images?.map((img: any) => {
      let url = img.image_url;
      if (!url) return null;
      
      try {
        // Handle bucket/path format (primary storage format)
        if (url.includes('/') && !url.startsWith('http')) {
          return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${url}`;
        }
        
        // Already a fully qualified URL, just ensure it's HTTPS
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url.replace(/^http:/, 'https:');
        }
        
        // Relative URL path, add origin
        if (url.startsWith('/')) {
          return `${window.location.origin}${url}`;
        }
        
        // Unknown format, try prefixing with Supabase storage URL
        return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${url}`;
      } catch (error) {
        console.error(`[Home] Error processing image URL: ${url}`, error);
        return null;
      }
    }).filter(Boolean) || [];
    
    // Extract tools data
    const tools = post.post_tools?.map((tool: any) => tool.tool) || [];
    
    return {
      id: post.id,
      user: {
        id: post.user_id,
        name: post.profiles?.display_name || 'User',
        username: `@${post.profiles?.username || 'user'}`,
        avatar_url: post.profiles?.avatar_url || '/placeholder.svg',
        level: post.profiles?.level || 1,
        experience: post.profiles?.experience || 0,
      },
      content: post.content,
      images: post.post_images?.length > 0 ? post.post_images.map((img: any) => ({ 
        image_url: img.image_url 
      })) : [],
      created_at: post.created_at,
      likes: post.likes_count || 0,
      upvotes: post.upvotes_count || 0,
      comments: post.comments_count || 0,
      shares: post.shares_count || 0,
      link_url: post.link_url,
      openForCollaboration: post.open_for_collaboration,
      // Note: tags would need to be fetched separately in a real implementation
      tags: [],
      tools: tools,
    };
  }, []);
  
  // Create a fetchPosts function that can be reused
  const fetchPosts = useCallback(async (offset = 0) => {
    try {
      if (offset === 0) {
        setIsLoadingPosts(true);
      } else {
        setIsLoadingMorePosts(true);
      }
      
      const limit = 20; // Number of posts to fetch per page
      console.log(`Fetching posts from database with offset ${offset}, limit ${limit}...`);
      const fetchedPosts = await getPosts(limit, offset);
      
      if (fetchedPosts) {
        console.log(`Fetched ${fetchedPosts.length} posts from database`);
        
        // Transform database posts to match the PostProps interface
        const formattedPosts = fetchedPosts.map(post => formatDatabasePost(post));
        
        if (offset === 0) {
          setPosts(formattedPosts);
        } else {
          setPosts(prevPosts => [...prevPosts, ...formattedPosts]);
        }
        
        // If we received fewer posts than requested, there are no more posts to load
        setHasMorePosts(fetchedPosts.length === limit);
        
        // Update the offset for the next fetch
        setPostsOffset(offset + fetchedPosts.length);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: "Error loading posts",
        description: "Could not load posts from the server.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingPosts(false);
      setIsLoadingMorePosts(false);
    }
  }, [formatDatabasePost]);

  // Function to fetch posts from followed users
  const fetchFollowingPosts = useCallback(async (offset = 0) => {
    if (!user) {
      setIsLoadingFollowingPosts(false);
      setIsLoadingMoreFollowingPosts(false);
      return;
    }

    try {
      if (offset === 0) {
        setIsLoadingFollowingPosts(true);
      } else {
        setIsLoadingMoreFollowingPosts(true);
      }
      
      const limit = 20; // Number of posts to fetch per page
      console.log(`Fetching posts from followed users with offset ${offset}, limit ${limit}...`);
      const fetchedPosts = await getFollowingPosts(user.id, limit, offset);
      
      if (fetchedPosts) {
        console.log(`Fetched ${fetchedPosts.length} posts from followed users`);
        
        // Transform database posts to match the PostProps interface
        const formattedPosts = fetchedPosts.map(post => formatDatabasePost(post));
        
        if (offset === 0) {
          setFollowingPosts(formattedPosts);
        } else {
          setFollowingPosts(prevPosts => [...prevPosts, ...formattedPosts]);
        }
        
        // If we received fewer posts than requested, there are no more posts to load
        setHasMoreFollowingPosts(fetchedPosts.length === limit);
        
        // Update the offset for the next fetch
        setFollowingPostsOffset(offset + fetchedPosts.length);
      } else {
        // Empty array is valid (user might not follow anyone or followed users have no posts)
        if (offset === 0) {
          setFollowingPosts([]);
        }
        setHasMoreFollowingPosts(false);
      }
    } catch (error) {
      console.error('Error fetching following posts:', error);
      toast({
        title: "Error loading following feed",
        description: "Could not load posts from users you follow.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingFollowingPosts(false);
      setIsLoadingMoreFollowingPosts(false);
    }
  }, [user, formatDatabasePost]);
  
  // Check for highlighted post from the location state (for collaboration widget)
  useEffect(() => {
    // Check location state
    if (location.state && location.state.highlightPostId) {
      setHighlightedPostId(location.state.highlightPostId);
    }

    // Also check URL query parameter
    const params = new URLSearchParams(location.search);
    const postIdParam = params.get('postId');
    if (postIdParam) {
      setHighlightedPostId(postIdParam);
    }
  }, [location.state, location.search]);
  
  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    // Disconnect any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // Create a new IntersectionObserver
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // Load more posts when the trigger element is visible
          if (activeTab === 'forYou' && hasMorePosts && !isLoadingMorePosts) {
            fetchPosts(postsOffset);
          } else if (activeTab === 'following' && hasMoreFollowingPosts && !isLoadingMoreFollowingPosts) {
            fetchFollowingPosts(followingPostsOffset);
          }
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the element is visible
    );
    
    // Observe the load more trigger element
    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }
    
    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    activeTab, 
    hasMorePosts, 
    hasMoreFollowingPosts, 
    isLoadingMorePosts, 
    isLoadingMoreFollowingPosts, 
    postsOffset,
    followingPostsOffset,
    fetchPosts,
    fetchFollowingPosts
  ]);
  
  // Scroll to highlighted post when posts are loaded
  useEffect(() => {
    if (highlightedPostId && !isLoadingPosts && posts.length > 0) {
      // First check if the post is in the current list
      const postExists = posts.some(post => post.id.toString() === highlightedPostId);
      
      if (postExists) {
        // Set a small timeout to allow the DOM to update
        setTimeout(() => {
          const postElement = document.getElementById(`post-${highlightedPostId}`);
          if (postElement) {
            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add a temporary highlight effect
            postElement.classList.add('bg-vibebops-highlight/20');
            setTimeout(() => {
              postElement.classList.remove('bg-vibebops-highlight/20');
              postElement.classList.add('transition-colors', 'duration-1000');
            }, 2000);
          }
        }, 100);
      } else {
        // If the post wasn't found in the current list, show a message
        toast({
          title: "Post not found",
          description: "The collaboration post you clicked on is not in the current feed. It may have been deleted or is older than the current posts shown.",
          variant: "destructive"
        });
      }
      
      // Clear the highlighted post ID after attempt
      setHighlightedPostId(null);
    }
  }, [highlightedPostId, isLoadingPosts, posts]);
  
  // Fetch posts and stats when component mounts
  useEffect(() => {
    // Fetch Stats
    const fetchHomeStats = async () => {
      setIsLoadingStats(true);
      try {
        const allStats = await getAnalyticsStats();
        setHomeStats({
          totalMembers: allStats.totalMembers,
          totalPosts: allStats.totalPosts,
          totalCollaborations: allStats.totalCollaborations,
        });
      } catch (error) {
        console.error('Error fetching home stats:', error);
        toast({
          title: "Error loading stats",
          description: "Could not load platform statistics.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingStats(false);
      }
    };
    
    // Fetch Chart Data
    const fetchChartData = async () => {
      setIsLoadingChartData(true);
      try {
        // Fetch all chart data in parallel
        const [postsData, membersData, collabData] = await Promise.all([
          getDailyPostsData(),
          getMonthlyMembersData(),
          getCollaborationData()
        ]);
        
        setPostsChartData(postsData);
        setMembersChartData(membersData);
        setCollaborationChartData(collabData);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        // Use empty data if there's an error
        setPostsChartData([]);
        setMembersChartData([]);
        setCollaborationChartData([]);
      } finally {
        setIsLoadingChartData(false);
      }
    };
    
    // Check for database issues
    const checkDatabase = async () => {
      try {
        // Simple check to see if post_images table exists by trying to query it
        const { data, error } = await supabase
          .from('post_images')
          .select('count')
          .limit(1);
        
        if (error) {
          console.error('[Home] Database error checking post_images table:', error);
          toast({
            title: "Database Structure Issue",
            description: "There may be problems displaying images - please contact support.",
            variant: "destructive"
          });
        } else {
          console.log('[Home] Database structure check passed:', data);
        }
      } catch (err) {
        console.error('[Home] Error checking database:', err);
      }
    };
    
    // Run checks and fetches
    checkDatabase();
    fetchPosts();
    fetchFollowingPosts();
    fetchHomeStats();
    fetchChartData();
    
    // Add an event listener to refresh posts and stats when the page regains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page is now visible, checking connection before refreshing...');
        
        // Ensure we don't have multiple refresh operations happening at once
        let refreshTimerRef: ReturnType<typeof setTimeout> | null = null;
        
        // First check if we have a valid connection
        const checkConnectionAndRefresh = async () => {
          try {
            // Check if we're connected to Supabase
            const { data } = await supabase.auth.getSession();
            const hasValidSession = !!data?.session;
            
            // If we have a session, proceed with refreshing data
            if (hasValidSession) {
              setIsLoadingPosts(true);
              setIsLoadingFollowingPosts(true);
              
              // Clear any existing timeout
              if (refreshTimerRef) clearTimeout(refreshTimerRef);
              
              // Use a short timeout to prevent UI flashing if data loads quickly
              refreshTimerRef = setTimeout(() => {
                console.log('Refreshing posts and stats...');
                fetchPosts();
                fetchFollowingPosts();
                fetchHomeStats();
                fetchChartData();
              }, 300);
            } else {
              console.warn('No valid session detected when tab became visible');
              // If we don't have a valid session, wait for the auth system to reconnect
              // This avoids showing loading state when the user will likely be redirected to login
            }
          } catch (error) {
            console.error('Error checking connection on visibility change:', error);
          }
        };
        
        // Start the check and refresh process
        checkConnectionAndRefresh();
        
        return () => {
          if (refreshTimerRef) clearTimeout(refreshTimerRef);
        };
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchPosts, fetchFollowingPosts]);
  
  // Set up real-time subscription to posts
  useEffect(() => {
    // Function to handle real-time post updates
    const handlePostUpdate = (post: any, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => {
      console.log(`Received real-time post ${eventType.toLowerCase()}:`, post);
      
      if (eventType === 'INSERT') {
        // Format the new post
        const formattedPost = formatDatabasePost(post);
        
        // Add the new post to both feeds
        setPosts(prevPosts => {
          // Check if post already exists to avoid duplicates
          const postExists = prevPosts.some(p => p.id === formattedPost.id);
          if (postExists) return prevPosts;
          
          // Show toast notification for new posts (except our own)
          if (user && post.user_id !== user.id) {
            toast({
              title: "New post",
              description: `${formattedPost.user.name} just posted something new!`,
            });
          }
          
          return [formattedPost, ...prevPosts];
        });
        
        // Add to following feed if the post is from a user we follow
        if (user && post.user_id !== user.id) {
          setFollowingPosts(prevPosts => {
            // Only add if user is in the following tab and the post doesn't already exist
            const postExists = prevPosts.some(p => p.id === formattedPost.id);
            if (postExists) return prevPosts;
            
            // We'd need to check if this user is followed, but for simplicity
            // we'll just add it to the list (in a real app, you'd verify this)
            return [formattedPost, ...prevPosts];
          });
        }
      } 
      else if (eventType === 'UPDATE') {
        // Format the updated post
        const formattedPost = formatDatabasePost(post);
        
        // Update the post in both feeds
        setPosts(prevPosts => 
          prevPosts.map(p => p.id === formattedPost.id ? formattedPost : p)
        );
        
        setFollowingPosts(prevPosts => 
          prevPosts.map(p => p.id === formattedPost.id ? formattedPost : p)
        );
      }
      else if (eventType === 'DELETE') {
        // Remove the deleted post from both feeds
        setPosts(prevPosts => 
          prevPosts.filter(p => p.id !== post.id)
        );
        
        setFollowingPosts(prevPosts => 
          prevPosts.filter(p => p.id !== post.id)
        );
      }
    };
    
    // Subscribe to post updates
    postsSubscriptionRef.current = subscribeToPosts(handlePostUpdate);
    
    // Cleanup subscription when component unmounts
    return () => {
      if (postsSubscriptionRef.current) {
        postsSubscriptionRef.current();
      }
    };
  }, [formatDatabasePost, user]);
  
  // Load initial data
  useEffect(() => {
    fetchPosts();
    fetchFollowingPosts();
    
    // ... rest of your existing code for fetching stats ...
    
  }, [fetchPosts, fetchFollowingPosts]);
  
  // Add a useEffect to refresh posts when connection state changes
  useEffect(() => {
    if (isConnected) {
      console.log('Connection restored or changed, refreshing posts');
      fetchPosts();
      fetchFollowingPosts();
    }
  }, [isConnected, fetchPosts, fetchFollowingPosts]);
  
  const handlePostCreated = (newPost: PostProps) => {
    console.log('Post created, manually adding to feed');
    
    // Optimistically add the post to the feed immediately
    setPosts(prevPosts => {
      // Check if post already exists to avoid duplicates
      const postExists = prevPosts.some(p => p.id === newPost.id);
      if (postExists) return prevPosts;
      
      return [newPost, ...prevPosts];
    });
    
    // For the user's own posts, also update the following feed if appropriate
    if (user && user.id === newPost.user.id) {
      setFollowingPosts(prevPosts => {
        // Check if post already exists to avoid duplicates
        const postExists = prevPosts.some(p => p.id === newPost.id);
        if (postExists) return prevPosts;
        
        return [newPost, ...prevPosts];
      });
    }
    
    // Update stats quietly without triggering a full refresh
    setHomeStats(prevStats => prevStats ? {
      ...prevStats,
      totalPosts: prevStats.totalPosts + 1,
      totalCollaborations: newPost.openForCollaboration ? prevStats.totalCollaborations + 1 : prevStats.totalCollaborations
    } : null);
    
    // Also update chart data for posts
    setPostsChartData(prevData => {
      if (!prevData || prevData.length === 0) return prevData;
      const newData = [...prevData];
      // Increment the most recent day's count
      if (newData.length > 0) {
        newData[newData.length - 1] = newData[newData.length - 1] + 1;
      }
      return newData;
    });
  };
  
  const handlePostDeleted = (postId: string | number) => {
    console.log('Post deleted, removing from feeds');
    
    // Remove the post from both feeds immediately for optimistic UI update
    setPosts(prevPosts => 
      prevPosts.filter(p => p.id.toString() !== postId.toString())
    );
    
    setFollowingPosts(prevPosts => 
      prevPosts.filter(p => p.id.toString() !== postId.toString())
    );
    
    // The real-time subscription will handle the server-side confirmation
  };
  
  // Function to refresh posts (for pull-to-refresh functionality)
  const refreshPosts = () => {
    console.log('Manually refreshing posts...');
    if (activeTab === 'following') {
      // Reset following posts
      setFollowingPostsOffset(0);
      setHasMoreFollowingPosts(true);
      fetchFollowingPosts(0);
    } else {
      // Reset regular posts
      setPostsOffset(0);
      setHasMorePosts(true);
      fetchPosts(0);
    }
    // fetchHomeStats();
  };
  
  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };
  
  return (
    <MainLayout>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard 
          title="Total Members"
          value={homeStats?.totalMembers.toString()}
          isLoading={isLoadingStats || isLoadingChartData}
          icon={Users}
          chartData={membersChartData}
        />
        <StatCard 
          title="Total Posts"
          value={homeStats?.totalPosts.toString()}
          isLoading={isLoadingStats || isLoadingChartData}
          icon={MessageCircle}
          chartData={postsChartData}
        />
        <StatCard 
          title="Collaborations"
          value={homeStats?.totalCollaborations.toString()}
          isLoading={isLoadingStats || isLoadingChartData}
          icon={Handshake}
          chartData={collaborationChartData}
        />
      </div>
      
      <div className="mb-6">
        <CreatePost onPostCreated={handlePostCreated} />
      </div>
      
      <Tabs defaultValue="forYou" className="mb-6" onValueChange={handleTabChange}>
        <div className="flex justify-between items-center">
          <TabsList className="bg-vibebops-card">
            <TabsTrigger value="forYou">For You</TabsTrigger>
            <TabsTrigger value="following">Following</TabsTrigger>
            <TabsTrigger value="trending">Trending</TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="forYou" className="mt-4">
          {isLoadingPosts && postsOffset === 0 ? (
            <div className="space-y-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : posts.length > 0 ? (
            <div className="space-y-4">
              {posts.map((post) => (
                <div 
                  key={post.id} 
                  id={`post-${post.id}`}
                  className="transition-all duration-300 rounded-lg"
                  ref={post.id.toString() === highlightedPostId ? highlightedPostRef : null}
                >
                  <Post post={post} onPostDeleted={handlePostDeleted} />
                </div>
              ))}
              
              {/* Loading indicator and trigger for infinite scroll */}
              <div ref={loadMoreTriggerRef} className="py-4 flex justify-center">
                {isLoadingMorePosts && hasMorePosts && (
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="h-5 w-5 animate-spin text-vibebops-primary" />
                    <span className="text-sm text-vibebops-textMuted">Loading more posts...</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-vibebops-textMuted">No posts found. Start creating!</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="following" className="mt-4">
          {isLoadingFollowingPosts && followingPostsOffset === 0 ? (
            <div className="space-y-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : followingPosts.length > 0 ? (
            <div className="space-y-4">
              {followingPosts.map((post) => (
                <div key={post.id} className="transition-all duration-300 rounded-lg">
                  <Post post={post} onPostDeleted={handlePostDeleted} />
                </div>
              ))}
              
              {/* Loading indicator and trigger for infinite scroll */}
              <div ref={loadMoreTriggerRef} className="py-4 flex justify-center">
                {isLoadingMoreFollowingPosts && hasMoreFollowingPosts && (
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="h-5 w-5 animate-spin text-vibebops-primary" />
                    <span className="text-sm text-vibebops-textMuted">Loading more posts...</span>
                  </div>
                )}
              </div>
            </div>
          ) : !user ? (
            <div className="text-center py-12">
              <p className="text-vibebops-textMuted">Please sign in to see posts from users you follow.</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-vibebops-textMuted mb-3">No posts from users you follow</p>
              <p className="text-sm text-vibebops-textMuted">Follow more creators to see their posts in this feed.</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="trending" className="mt-4 text-center py-12">
          <p className="text-vibebops-textMuted">Trending posts coming soon!</p>
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
};

export default Home;
